import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  Loader2, ArrowLeft, CheckCircle2, Clock, AlertTriangle,
  RefreshCw, BarChart3, TrendingUp, TrendingDown, Minus,
  Shield, Truck, ChevronRight, Eye, Filter, Star, Activity
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

const RISK_CONFIG = {
  low: { label: "Low Risk", color: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-400/20", bar: "bg-emerald-400" },
  medium: { label: "Medium", color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20", bar: "bg-amber-400" },
  high: { label: "High Risk", color: "text-orange-400", bg: "bg-orange-400/10", border: "border-orange-400/20", bar: "bg-orange-400" },
  critical: { label: "Critical", color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/20", bar: "bg-red-500" },
};

const METRIC_LABELS = {
  scar_frequency: "SCAR Frequency",
  severity: "Severity Profile",
  response_timeliness: "Response Time",
  effectiveness: "Effectiveness",
  assessment: "Assessment Score",
};

const SupplierScorecardPage = () => {
  const { t } = useTranslation('operations');
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [filterRisk, setFilterRisk] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/supplier-scorecard/`;
      if (filterRisk) url += `?risk_level=${filterRisk}`;
      const res = await ax.get(url);
      setData(res.data);
    } catch (err) {
      console.error("Failed to load scorecards:", err);
    }
    setLoading(false);
  }, [filterRisk]);

  useEffect(() => { loadData(); }, [loadData]);

  const openDetail = async (supplierName) => {
    try {
      const res = await ax.get(`${API}/supplier-scorecard/${encodeURIComponent(supplierName)}`);
      setDetailData(res.data);
      setSelectedSupplier(supplierName);
    } catch (err) {
      alert("Failed to load: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <QMSLayout title={t('scorecard.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  /* ═══ DETAIL VIEW ═══ */
  if (selectedSupplier && detailData) {
    const risk = RISK_CONFIG[detailData.risk_level] || RISK_CONFIG.medium;
    const bd = detailData.breakdown || {};

    return (
      <QMSLayout title={`${t('scorecard.title')}: ${selectedSupplier}`}>
        <div className="space-y-5" data-testid="scorecard-detail">
          <button onClick={() => { setSelectedSupplier(null); setDetailData(null); }}
            className="flex items-center gap-2 text-sm text-[#8B95A5] hover:text-white" data-testid="scorecard-detail-back">
            <ArrowLeft className="w-4 h-4" /> {t('common:actions.back')}
          </button>

          {/* Score Header */}
          <div className={`${risk.bg} border ${risk.border} rounded-lg p-6 flex items-center justify-between`} data-testid="score-header">
            <div className="flex items-center gap-5">
              <div className="relative">
                <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#2D313D" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="currentColor"
                    className={risk.color} strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={`${detailData.composite_score}, 100`} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-xl font-bold ${risk.color}`}>{detailData.composite_score}</span>
                </div>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{detailData.supplier_name}</h2>
                <span className={`text-sm ${risk.color}`}>{risk.label}</span>
              </div>
            </div>
            <div className="text-right text-xs text-[#8B95A5]">
              <p>Calculated: {new Date(detailData.calculated_at).toLocaleString()}</p>
              {detailData.last_scar_date && <p>Last SCAR: {new Date(detailData.last_scar_date).toLocaleDateString()}</p>}
            </div>
          </div>

          {/* Metric Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {Object.entries(bd).map(([key, metric]) => {
              const score = metric.score;
              const color = score >= 75 ? "text-emerald-400" : score >= 50 ? "text-amber-400" : "text-red-400";
              const barColor = score >= 75 ? "bg-emerald-400" : score >= 50 ? "bg-amber-400" : "bg-red-400";
              return (
                <div key={key} className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4" data-testid={`metric-${key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#8B95A5]">{METRIC_LABELS[key] || key}</span>
                    <span className={`text-sm font-bold ${color}`}>{score}</span>
                  </div>
                  <div className="w-full h-1.5 bg-[#2D313D] rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${score}%` }} />
                  </div>
                  <div className="mt-2 text-[10px] text-[#8B95A5]">
                    <span className="text-[#CBD5E1]">Weight: {Math.round(metric.weight * 100)}%</span>
                    {key === "scar_frequency" && <span className="ml-2">{metric.total_scars} SCARs</span>}
                    {key === "severity" && <span className="ml-2">C:{metric.critical} M:{metric.major} m:{metric.minor}</span>}
                    {key === "response_timeliness" && metric.avg_days !== null && <span className="ml-2">Avg: {metric.avg_days}d</span>}
                    {key === "effectiveness" && <span className="ml-2">{metric.effective_first_pass}/{metric.verified} first-pass</span>}
                    {key === "assessment" && <span className="ml-2">{metric.assessments_count} assessments</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* SCAR Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MiniCard label="Total SCARs" value={detailData.scar_summary?.total || 0} color="text-[#36A9E1]" />
            <MiniCard label="Open" value={detailData.scar_summary?.open || 0} color="text-amber-400" />
            <MiniCard label="Closed" value={detailData.scar_summary?.closed || 0} color="text-emerald-400" />
            <MiniCard label="Overdue" value={detailData.scar_summary?.overdue || 0} color="text-red-400" />
            <MiniCard label="Cancelled" value={detailData.scar_summary?.cancelled || 0} color="text-[#8B95A5]" />
          </div>

          {/* Linked SCARs */}
          {detailData.linked_scars?.length > 0 && (
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Linked SCARs</h3>
              <div className="space-y-2">
                {detailData.linked_scars.map(s => {
                  const stColor = s.status === "closed" ? "text-emerald-400" : s.status === "cancelled" ? "text-[#8B95A5]" : "text-amber-400";
                  const prColor = s.priority === "critical" ? "text-red-500" : s.priority === "major" ? "text-amber-400" : "text-[#36A9E1]";
                  return (
                    <div key={s.scar_id} className="flex items-center justify-between px-3 py-2 bg-[#181B23] rounded text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-[#8B95A5] font-mono">{s.scar_id}</span>
                        <span className={stColor}>{s.status}</span>
                        <span className={prColor}>{s.priority}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#8B95A5]">
                        <span className="truncate max-w-xs">{s.description}</span>
                        <span>{s.due_date || ""}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Linked Assessments */}
          {detailData.linked_assessments?.length > 0 && (
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Linked Assessments</h3>
              <div className="space-y-2">
                {detailData.linked_assessments.map(a => (
                  <div key={a.submission_id} className="flex items-center justify-between px-3 py-2 bg-[#181B23] rounded text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-[#8B95A5] font-mono">{a.submission_id}</span>
                      <span className="text-white">{a.template_name}</span>
                    </div>
                    <span className="text-[#8B95A5]">{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </QMSLayout>
    );
  }

  /* ═══ LIST VIEW ═══ */
  const scorecards = data?.scorecards || [];
  const dist = data?.risk_distribution || {};

  return (
    <QMSLayout title={t('operations:scorecard.title')}>
      <div className="space-y-5" data-testid="supplier-scorecard">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 flex items-center gap-3" data-testid="stat-total-suppliers">
            <Truck className="w-5 h-5 text-[#36A9E1]" />
            <div>
              <div className="text-xl font-bold text-white">{data?.total_suppliers || 0}</div>
              <div className="text-xs text-[#8B95A5]">Suppliers</div>
            </div>
          </div>
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 flex items-center gap-3" data-testid="stat-avg-score">
            <Star className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-xl font-bold text-white">{data?.average_score || 0}</div>
              <div className="text-xs text-[#8B95A5]">Avg Score</div>
            </div>
          </div>
          {Object.entries(RISK_CONFIG).map(([key, cfg]) => (
            <div key={key} className={`${cfg.bg} border ${cfg.border} rounded-lg p-4 flex items-center gap-3 cursor-pointer hover:opacity-80`}
              onClick={() => setFilterRisk(filterRisk === key ? "" : key)} data-testid={`stat-risk-${key}`}>
              <Shield className={`w-5 h-5 ${cfg.color}`} />
              <div>
                <div className={`text-xl font-bold ${cfg.color}`}>{dist[key] || 0}</div>
                <div className="text-xs text-[#8B95A5]">{cfg.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {filterRisk && (
              <button onClick={() => setFilterRisk("")}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#36A9E1]/10 text-[#36A9E1] rounded text-xs">
                Clear Filter
              </button>
            )}
          </div>
          <button onClick={loadData} className="p-2 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white" data-testid="refresh-scorecards">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Scorecard List */}
        {scorecards.length === 0 ? (
          <div className="text-center py-16 text-[#8B95A5]">
            <Truck className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No supplier scorecards available</p>
            <p className="text-xs mt-1">Scorecards are auto-generated from SCAR and Supplier Assessment data</p>
          </div>
        ) : (
          <div className="space-y-2">
            {scorecards.map(sc => {
              const risk = RISK_CONFIG[sc.risk_level] || RISK_CONFIG.medium;
              return (
                <div key={sc.supplier_name}
                  className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 hover:border-[#36A9E1]/20 transition-all cursor-pointer"
                  onClick={() => openDetail(sc.supplier_name)}
                  data-testid={`scorecard-${sc.supplier_name.replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Score circle */}
                      <div className="relative flex-shrink-0">
                        <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#2D313D" strokeWidth="2.5" />
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="currentColor"
                            className={risk.color} strokeWidth="2.5" strokeLinecap="round"
                            strokeDasharray={`${sc.composite_score}, 100`} />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className={`text-xs font-bold ${risk.color}`}>{sc.composite_score}</span>
                        </div>
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-sm font-medium text-white truncate">{sc.supplier_name}</h3>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${risk.bg} ${risk.color}`}>{risk.label}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[#8B95A5]">
                          <span>{sc.scar_summary.total} SCARs</span>
                          {sc.scar_summary.open > 0 && <span className="text-amber-400">{sc.scar_summary.open} open</span>}
                          {sc.scar_summary.overdue > 0 && <span className="text-red-400">{sc.scar_summary.overdue} overdue</span>}
                        </div>
                      </div>

                      {/* Mini metric bars */}
                      <div className="hidden md:flex items-center gap-2">
                        {Object.entries(sc.breakdown).map(([key, m]) => {
                          const barColor = m.score >= 75 ? "bg-emerald-400" : m.score >= 50 ? "bg-amber-400" : "bg-red-400";
                          return (
                            <div key={key} className="w-12" title={`${METRIC_LABELS[key]}: ${m.score}`}>
                              <div className="w-full h-1 bg-[#2D313D] rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${m.score}%` }} />
                              </div>
                              <div className="text-[8px] text-[#8B95A5] text-center mt-0.5">{m.score}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <ChevronRight className="w-4 h-4 text-[#8B95A5] ml-3 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Scoring Methodology */}
        <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5" data-testid="scoring-methodology">
          <h3 className="text-xs font-semibold text-[#8B95A5] uppercase tracking-wider mb-3">Scoring Methodology</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
            <MethodCard title="SCAR Frequency" weight="20%" desc="Fewer SCARs = higher score" />
            <MethodCard title="Severity Profile" weight="25%" desc="Minor > Major > Critical" />
            <MethodCard title="Response Time" weight="20%" desc="Faster response = higher" />
            <MethodCard title="Effectiveness" weight="20%" desc="First-pass close rate" />
            <MethodCard title="Assessment" weight="15%" desc="Supplier audit ratings" />
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

const MiniCard = ({ label, value, color }) => (
  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-3 text-center">
    <div className={`text-lg font-bold ${color}`}>{value}</div>
    <div className="text-[10px] text-[#8B95A5]">{label}</div>
  </div>
);

const MethodCard = ({ title, weight, desc }) => (
  <div className="bg-[#181B23] rounded p-3 text-center">
    <div className="text-[#36A9E1] font-medium text-xs mb-0.5">{title}</div>
    <div className="text-white text-sm font-bold">{weight}</div>
    <div className="text-[10px] text-[#8B95A5] mt-0.5">{desc}</div>
  </div>
);

export default SupplierScorecardPage;
